/**
 * Ecrane — Digital Display.
 *
 * Pagina care leagă televizorul din sală de meniul din dashboard. Fluxul, așa cum îl trăiește
 * restaurantul:
 *
 *   1. deschide playerul pe televizor (`/display/player`) → ecranul afișează un cod ABC-7291;
 *   2. apasă „Conectează un ecran", scrie codul, alege restaurantul și locația → gata;
 *   3. de aici încolo ecranul afișează meniul publicat și se actualizează singur, la fiecare
 *      modificare, fără ca cineva să atingă televizorul.
 *
 * Pagina spune și ce NU merge: un ecran care nu a mai trimis semnal este marcat „offline" după 90 de
 * secunde și apare cu momentul ultimei comunicări — fără să fie nevoie de ghicit.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  Input,
  KpiCard,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  StatusDot,
  useToast,
} from '../components/ui.js';
import { DeviceDesignModal } from '../components/DeviceDesignModal.js';
import { api } from '../lib/api.js';
import { relativeTime, useResource } from '../lib/hooks.js';
import { errorMessage, fieldErrors, useSession } from '../lib/session.js';

interface Device {
  id: string;
  public_id: string;
  name: string;
  zone: string | null;
  /** Fusul orar al ecranului — se afișează în programări, ca ora să fie fără dubii. */
  timezone: string;
  status: 'pending' | 'online' | 'offline' | 'disabled';
  orientation: 'landscape' | 'portrait';
  resolution: string | null;
  player_version: string | null;
  last_seen_at: string | null;
  offline_since: string | null;
  paired_at: string | null;
  location_id: string | null;
  location_name: string | null;
  uptime_seconds: number | null;
  pending_commands: number;
  /** `menu` = meniul sălii, `kitchen` = board-ul de comenzi din bucătărie. */
  content_mode: 'menu' | 'kitchen';
  kds_config: { warnAfterMinutes?: number; sound?: boolean; actionsEnabled?: boolean };
}

/** Valorile implicite ale board-ului de bucătărie (aceleași ca în server). */
const KDS_DEFAULTS = { warnAfterMinutes: 12, sound: true, actionsEnabled: true };

interface Stats {
  total: number;
  online: number;
  offline: number;
  disabled: number;
  offlineOverDay: number;
  avgUptimeHours: number | null;
}

interface CommandRow {
  id: string;
  command: string;
  payload: Record<string, unknown>;
  status: 'pending' | 'sent' | 'acknowledged' | 'done' | 'failed' | 'expired';
  issued_at: string;
  acknowledged_at: string | null;
}

interface StatusEvent {
  from_status: string | null;
  to_status: string;
  reason: string | null;
  occurred_at: string;
}

interface Restaurant {
  id: string;
  name: string;
}

interface Location {
  id: string;
  name: string;
  restaurant_id: string;
  is_active: boolean;
}

const COMMAND_LABELS: Record<string, string> = {
  refresh: 'Reîmprospătează meniul',
  reload_cache: 'Reîncarcă memoria ecranului',
  screenshot: 'Captură de ecran',
  set_volume: 'Setează volumul',
  set_brightness: 'Setează luminozitatea',
  screensaver: 'Mod repaus',
  reboot: 'Repornește playerul',
};

const STATUS_LABEL: Record<string, string> = {
  online: 'Online',
  offline: 'Offline',
  pending: 'Se conectează',
  disabled: 'Dezactivat',
};

const REASON_LABEL: Record<string, string> = {
  heartbeat: 'semnal primit',
  heartbeat_timeout: 'fără semnal',
  paired: 'conectat',
  offline: 'fără semnal',
  disabled: 'dezactivat',
  manual: 'manual',
};

const COMMAND_STATUS_LABEL: Record<string, string> = {
  pending: 'în așteptare',
  sent: 'trimisă',
  acknowledged: 'preluată',
  done: 'executată',
  failed: 'eșuată',
  expired: 'expirată',
};

/** Formatarea codului scris de om: „abc7291" → „ABC-7291". */
function formatCode(value: string): string {
  const clean = value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 7);
  return clean.length > 3 ? `${clean.slice(0, 3)}-${clean.slice(3)}` : clean;
}

function uptimeLabel(hours: number | null | undefined): string {
  if (!hours || hours <= 0) return '—';
  if (hours < 24) return `${hours.toFixed(1)} h`;
  return `${Math.round(hours / 24)} z`;
}

export function DisplaysPage() {
  const session = useSession();
  const toast = useToast();
  const devicesResource = useResource<{ devices: Device[]; stats: Stats }>(async () => {
    const response = await api.getRaw<{ data: Device[]; meta: { stats: Stats } }>('/displays');
    // Serverul trimite sumarul sub `meta.stats` (și pragul de offline lângă el): KPI-urile se citesc
    // de acolo, nu din `meta` direct.
    return { devices: response.data, stats: response.meta.stats };
  }, []);

  const restaurants = useResource<Restaurant[]>(() => api.get<Restaurant[]>('/restaurants'), []);
  const locations = useResource<Location[]>(() => api.get<Location[]>('/locations'), []);

  const [pairOpen, setPairOpen] = useState(false);
  const [editing, setEditing] = useState<Device | null>(null);
  const [detail, setDetail] = useState<Device | null>(null);
  const [design, setDesign] = useState<Device | null>(null);
  const [disconnecting, setDisconnecting] = useState<Device | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [code, setCode] = useState('');
  const [restaurantId, setRestaurantId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [name, setName] = useState('');
  const [zone, setZone] = useState('');
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape');

  const canCreate = session.can('display.create');
  const canCommand = session.can('display.command');

  const devices = devicesResource.data?.devices ?? [];
  const stats = devicesResource.data?.stats;

  const setData = devicesResource.setData;
  /**
   * Reîmprospătare discretă la fiecare 15 secunde.
   *
   * Starea ecranelor se schimbă singură (un televizor se stinge, altul revine), iar pragul de
   * offline este de 90 de secunde: fără reîmprospătare, pagina ar arăta o realitate veche. Nu
   * pornim spinnerul — datele vechi rămân pe ecran până vin cele noi.
   */
  useEffect(() => {
    const timer = window.setInterval(async () => {
      try {
        const response = await api.getRaw<{ data: Device[]; meta: { stats: Stats } }>('/displays');
        setData({ devices: response.data, stats: response.meta.stats });
      } catch {
        /* o reîmprospătare discretă nu deranjează utilizatorul dacă eșuează */
      }
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [setData]);

  const reload = devicesResource.reload;
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  const locationOptions = useMemo(
    () =>
      (locations.data ?? []).filter(
        (location) => location.is_active && (!restaurantId || location.restaurant_id === restaurantId),
      ),
    [locations.data, restaurantId],
  );

  async function claim() {
    setBusy(true);
    setFormError(null);
    setErrors({});
    try {
      const device = await api.post<Device>('/displays/pairing/claim', {
        code,
        restaurantId,
        locationId: locationId || null,
        name: name || undefined,
        zone: zone || null,
        orientation,
      });
      toast.success(`${device.name} a fost conectat`, 'Ecranul afișează meniul în câteva secunde.');
      setPairOpen(false);
      setCode('');
      setName('');
      setZone('');
      await reloadRef.current();
    } catch (err) {
      setErrors(fieldErrors(err));
      setFormError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function runCommand(device: Device, command: string, payload: Record<string, unknown> = {}) {
    try {
      const result = await api.post<{ immediate: boolean }>(`/displays/${device.id}/commands`, { command, payload });
      toast.success(
        `${COMMAND_LABELS[command] ?? command} — trimisă`,
        result.immediate
          ? `${device.name} o execută imediat.`
          : `${device.name} o preia la următorul semnal (cel mult 30 de secunde).`,
      );
      void reloadRef.current();
    } catch (err) {
      toast.error('Comanda nu a putut fi trimisă', errorMessage(err));
    }
  }

  async function disconnect() {
    if (!disconnecting) return;
    setBusy(true);
    try {
      await api.del(`/displays/${disconnecting.id}`);
      toast.success(`${disconnecting.name} a fost deconectat`, 'Poți reconecta oricând ecranul, cu un cod nou.');
      setDisconnecting(null);
      await reloadRef.current();
    } catch (err) {
      toast.error('Deconectarea nu a reușit', errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Ecrane"
        subtitle="Televizoarele din sală care afișează meniul sau ecranele din bucătărie care afișează comenzile. Se actualizează automat la fiecare modificare din dashboard."
        actions={
          canCreate ? (
            <Button onClick={() => setPairOpen(true)}>
              <Icon name="plus" size={16} />
              Conectează un ecran
            </Button>
          ) : null
        }
      />

      {devicesResource.error ? (
        <InlineAlert tone="danger" title="Nu am putut încărca ecranele">
          {devicesResource.error}{' '}
          <button type="button" className="underline" onClick={() => void devicesResource.reload()}>
            Reîncearcă
          </button>
        </InlineAlert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {!stats ? (
          <>
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </>
        ) : (
          <>
            <KpiCard label="Ecrane conectate" value={stats.total} hint="în cont" />
            <KpiCard label="Online acum" value={stats.online} hint={stats.total ? `din ${stats.total}` : 'niciun ecran conectat'} />
            <KpiCard
              label="Offline"
              value={stats.offline}
              hint={stats.offlineOverDay ? `${stats.offlineOverDay} de peste o zi` : 'verificare la 90 de secunde'}
            />
            <KpiCard label="Disponibilitate (7 zile)" value={uptimeLabel(stats.avgUptimeHours)} hint="media sesiunilor active" />
          </>
        )}
      </div>

      {!devicesResource.loading && devices.length === 0 ? (
        <Card className="flex flex-col gap-6">
          <EmptyState
            title="Niciun ecran conectat încă"
            description="Conectarea unui televizor durează sub un minut și nu are nevoie de instalare."
            action={
              canCreate ? (
                <Button onClick={() => setPairOpen(true)}>
                  <Icon name="plus" size={16} />
                  Conectează un ecran
                </Button>
              ) : null
            }
          />
          <ol className="grid gap-4 sm:grid-cols-3">
            {[
              {
                title: '1. Deschide playerul pe televizor',
                text: 'În browserul televizorului sau al stick-ului, intră pe adresa playerului și lasă pagina deschisă.',
              },
              {
                title: '2. Notează codul afișat',
                text: 'Ecranul arată un cod de forma ABC-7291, valabil 10 minute, apoi primește automat altul.',
              },
              {
                title: '3. Revendică-l aici',
                text: 'Apasă „Conectează un ecran", scrie codul și alege restaurantul și locația.',
              },
            ].map((step) => (
              <li key={step.title} className="flex flex-col gap-1 rounded-[var(--radius-md)] bg-surface-3 p-4">
                <span className="font-medium text-ink-900">{step.title}</span>
                <span className="text-body-s text-ink-600">{step.text}</span>
              </li>
            ))}
          </ol>
        </Card>
      ) : null}

      {devices.length > 0 ? (
        <div className="grid gap-4 xl:grid-cols-2">
          {devices.map((device) => (
            <DeviceCard
              key={device.id}
              device={device}
              canCommand={canCommand}
              onCommand={runCommand}
              onEdit={() => setEditing(device)}
              onDetails={() => setDetail(device)}
              onDesign={() => setDesign(device)}
              onDisconnect={() => setDisconnecting(device)}
            />
          ))}
        </div>
      ) : null}

      {design ? <DeviceDesignModal device={design} onClose={() => setDesign(null)} /> : null}

      <Card className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2 text-body-s text-ink-600">
          <Icon name="display" size={16} />
          <span className="font-medium text-ink-900">Adresa playerului</span>
          <code className="rounded-[var(--radius-sm)] bg-surface-3 px-2 py-0.5 text-caption">/display/player</code>
          <span>— o deschizi pe televizor sau o salvezi în favorite pe stick-ul TV.</span>
        </div>
        <p className="text-body-s text-ink-600">
          Un ecran este considerat offline dacă nu a mai trimis semnal 90 de secunde. Chiar și atunci, meniul continuă
          să ruleze din ultima sincronizare, iar la revenirea conexiunii ecranul se actualizează singur.
        </p>
      </Card>

      {/* ────────────────── conectarea unui ecran ────────────────── */}
      <Modal
        open={pairOpen}
        onClose={() => setPairOpen(false)}
        title="Conectează un ecran"
        description="Scrie codul afișat pe televizor și alege unde se află."
        footer={
          <>
            <Button variant="ghost" onClick={() => setPairOpen(false)}>
              Renunță
            </Button>
            <Button onClick={claim} loading={busy} disabled={code.length !== 8 || !restaurantId}>
              Conectează
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError ? (
            <InlineAlert tone="danger" title="Nu am putut conecta ecranul">
              {formError}
            </InlineAlert>
          ) : null}

          <Field label="Codul de pe ecran" hint="Are forma ABC-7291 și expiră după 10 minute." required errors={errors.code}>
            {(props) => (
              <Input
                {...props}
                value={code}
                onChange={(event) => setCode(formatCode(event.target.value))}
                placeholder="ABC-7291"
                inputMode="text"
                autoComplete="off"
                spellCheck={false}
                className="nums text-center text-title tracking-[0.28em]"
              />
            )}
          </Field>

          <Field label="Restaurant" required errors={errors.restaurantId}>
            {(props) => (
              <Select
                {...props}
                value={restaurantId}
                onChange={(event) => {
                  setRestaurantId(event.target.value);
                  setLocationId('');
                }}
              >
                <option value="">Alege restaurantul…</option>
                {(restaurants.data ?? []).map((restaurant) => (
                  <option key={restaurant.id} value={restaurant.id}>
                    {restaurant.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Locație" hint="Opțional" errors={errors.locationId}>
              {(props) => (
                <Select {...props} value={locationId} onChange={(event) => setLocationId(event.target.value)} disabled={!restaurantId}>
                  <option value="">Toate locațiile</option>
                  {locationOptions.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Orientarea ecranului" hint="Cum este montat televizorul">
              {(props) => (
                <Select {...props} value={orientation} onChange={(event) => setOrientation(event.target.value as 'landscape' | 'portrait')}>
                  <option value="landscape">Orizontal (landscape)</option>
                  <option value="portrait">Vertical (portrait)</option>
                </Select>
              )}
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nume" hint="Opțional — apare în dashboard" errors={errors.name}>
              {(props) => (
                <Input {...props} value={name} onChange={(event) => setName(event.target.value)} placeholder="Ex.: Bar, Terasă, Intrarea" />
              )}
            </Field>
            <Field label="Zonă" hint="Opțional — se afișează pe ecran" errors={errors.zone}>
              {(props) => (
                <Input {...props} value={zone} onChange={(event) => setZone(event.target.value)} placeholder="Ex.: Terasa de vară" />
              )}
            </Field>
          </div>

          <InlineAlert tone="info" title="Ecranul nu are nevoie de instalare">
            Playerul rulează în browserul televizorului sau al stick-ului. După conectare, meniul apare în câteva
            secunde și rămâne sincronizat cu dashboard-ul.
          </InlineAlert>
        </div>
      </Modal>

      {/* ────────────────── editarea unui ecran ────────────────── */}
      <EditDeviceModal
        device={editing}
        locations={locations.data ?? []}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await reloadRef.current();
        }}
      />

      {/* ────────────────── fișa ecranului ────────────────── */}
      <DeviceDetailModal device={detail} onClose={() => setDetail(null)} onCommand={runCommand} canCommand={canCommand} />

      <ConfirmDialog
        open={Boolean(disconnecting)}
        onClose={() => setDisconnecting(null)}
        onConfirm={disconnect}
        loading={busy}
        title={disconnecting ? `Deconectezi ${disconnecting.name}?` : 'Deconectezi ecranul?'}
        description="Ecranul își pierde accesul și revine la ecranul de conectare. Poți reconecta televizorul oricând, cu un cod nou — meniul și setările restaurantului nu se ating."
        confirmLabel="Deconectează"
      />
    </div>
  );
}

/* ───────────────────────────── cardul unui ecran ───────────────────────────── */

function DeviceCard({
  device,
  canCommand,
  onCommand,
  onEdit,
  onDetails,
  onDesign,
  onDisconnect,
}: {
  device: Device;
  canCommand: boolean;
  onCommand: (device: Device, command: string, payload?: Record<string, unknown>) => void;
  onEdit: () => void;
  onDetails: () => void;
  onDesign: () => void;
  onDisconnect: () => void;
}) {
  const toast = useToast();
  const online = device.status === 'online';
  const playerUrl = `/display/player/${device.public_id}`;

  /** Deschide playerul acestui ecran: cel mai simplu mod de a verifica ce vede restaurantul. */
  function openPlayer() {
    window.open(playerUrl, '_blank', 'noopener');
  }

  async function copyLink() {
    const absolute = `${window.location.origin}${playerUrl}`;
    try {
      await navigator.clipboard.writeText(absolute);
      toast.success('Link copiat', 'Îl poți deschide pe televizor sau salva în favorite.');
    } catch {
      toast.error('Copiază manual linkul', absolute);
    }
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="mt-1.5">
            <StatusDot tone={online ? 'success' : device.status === 'disabled' ? 'neutral' : 'danger'} pulse={online} />
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="font-display text-title text-ink-950">{device.name}</span>
            <span className="text-body-s text-ink-600">
              {[device.location_name, device.zone].filter(Boolean).join(' · ') || 'Fără locație atribuită'}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {device.content_mode === 'kitchen' ? <Badge tone="brand">Bucătărie</Badge> : null}
          <Badge tone={online ? 'success' : device.status === 'disabled' ? 'neutral' : 'danger'}>
            {STATUS_LABEL[device.status] ?? device.status}
          </Badge>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-body-s sm:grid-cols-3">
        <Info label="Ultimul semnal">{device.last_seen_at ? relativeTime(device.last_seen_at) : 'niciodată'}</Info>
        <Info label="Sesiune activă">{device.uptime_seconds ? `${Math.round(device.uptime_seconds / 60)} min` : '—'}</Info>
        <Info label="Rezoluție">{device.resolution ?? '—'}</Info>
        <Info label="Orientare">{device.orientation === 'portrait' ? 'Vertical' : 'Orizontal'}</Info>
        <Info label="Player">{device.player_version ? `v${device.player_version}` : '—'}</Info>
        <Info label="Conectat">{device.paired_at ? relativeTime(device.paired_at) : '—'}</Info>
      </dl>

      {device.pending_commands > 0 ? (
        <InlineAlert tone="warning" title={`${device.pending_commands} comandă în așteptare`}>
          Se livrează la următorul semnal al ecranului (cel mult 30 de secunde). Dacă televizorul este oprit, comanda
          așteaptă până la repornire.
        </InlineAlert>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" onClick={openPlayer}>
          <Icon name="external" size={15} />
          Deschide playerul
        </Button>
        {canCommand ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => onCommand(device, 'refresh')}>
              Reîmprospătează
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onCommand(device, 'screensaver')}>
              Mod repaus
            </Button>
          </>
        ) : null}
        <Button size="sm" variant="ghost" onClick={copyLink}>
          <Icon name="copy" size={15} />
          Copiază linkul
        </Button>
        <Button size="sm" variant="ghost" onClick={onDesign}>
          <Icon name="sparkles" size={15} />
          Program & design
        </Button>
        <Button size="sm" variant="ghost" onClick={onDetails}>
          Detalii
        </Button>
        {canCommand ? (
          <Button size="sm" variant="ghost" onClick={onEdit}>
            Setări
          </Button>
        ) : null}
        <Button size="sm" variant="danger" onClick={onDisconnect}>
          Deconectează
        </Button>
      </div>
    </Card>
  );
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col">
      <dt className="text-caption uppercase tracking-wide text-ink-400">{label}</dt>
      <dd className="truncate text-ink-900">{children}</dd>
    </div>
  );
}

/* ────────────────────────── setările unui ecran ────────────────────────── */

function EditDeviceModal({
  device,
  locations,
  onClose,
  onSaved,
}: {
  device: Device | null;
  locations: Location[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [zone, setZone] = useState('');
  const [locationId, setLocationId] = useState('');
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape');
  const [contentMode, setContentMode] = useState<'menu' | 'kitchen'>('menu');
  const [warnAfter, setWarnAfter] = useState(KDS_DEFAULTS.warnAfterMinutes);
  const [kdsSound, setKdsSound] = useState(KDS_DEFAULTS.sound);
  const [kdsActions, setKdsActions] = useState(KDS_DEFAULTS.actionsEnabled);
  const [brightness, setBrightness] = useState(100);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!device) return;
    setName(device.name);
    setZone(device.zone ?? '');
    setLocationId(device.location_id ?? '');
    setOrientation(device.orientation);
    setContentMode(device.content_mode ?? 'menu');
    setWarnAfter(device.kds_config?.warnAfterMinutes ?? KDS_DEFAULTS.warnAfterMinutes);
    setKdsSound(device.kds_config?.sound ?? KDS_DEFAULTS.sound);
    setKdsActions(device.kds_config?.actionsEnabled ?? KDS_DEFAULTS.actionsEnabled);
    setError(null);
  }, [device]);

  if (!device) return null;
  const current = device;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/displays/${current.id}`, {
        name,
        zone: zone || null,
        locationId: locationId || null,
        orientation,
        contentMode,
        kdsConfig: { warnAfterMinutes: warnAfter, sound: kdsSound, actionsEnabled: kdsActions },
      });
      toast.success('Setările au fost salvate', 'Ecranul le preia imediat.');
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function applyBrightness() {
    try {
      await api.post(`/displays/${current.id}/commands`, { command: 'set_brightness', payload: { value: brightness } });
      toast.success(`Luminozitatea a fost setată la ${brightness}%`);
    } catch (err) {
      toast.error('Comanda nu a putut fi trimisă', errorMessage(err));
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Setările pentru ${current.name}`}
      description="Numele și zona apar în dashboard; orientarea schimbă felul în care este așezat conținutul pe ecran."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Renunță
          </Button>
          <Button onClick={save} loading={busy}>
            Salvează
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <InlineAlert tone="danger" title="Nu am putut salva">
            {error}
          </InlineAlert>
        ) : null}

        <Field label="Nume">
          {(props) => <Input {...props} value={name} onChange={(event) => setName(event.target.value)} />}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Zonă" hint="Se afișează pe ecran, sub numele restaurantului">
            {(props) => (
              <Input {...props} value={zone} onChange={(event) => setZone(event.target.value)} placeholder="Ex.: Terasa de vară" />
            )}
          </Field>
          <Field label="Locație">
            {(props) => (
              <Select {...props} value={locationId} onChange={(event) => setLocationId(event.target.value)}>
                <option value="">Toate locațiile</option>
                {locations
                  .filter((location) => location.is_active || location.id === locationId)
                  .map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.is_active ? location.name : `${location.name} (inactivă)`}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <Field label="Orientare">
          {(props) => (
            <Select {...props} value={orientation} onChange={(event) => setOrientation(event.target.value as 'landscape' | 'portrait')}>
              <option value="landscape">Orizontal (landscape)</option>
              <option value="portrait">Vertical (portrait)</option>
            </Select>
          )}
        </Field>

        {/*
          Ce afișează ecranul: meniul sălii sau comenzile bucătăriei.

          Este cea mai importantă setare a unui ecran — un televizor din sală care arată comenzi, sau
          unul din bucătărie care arată meniul, este un ecran nefolosit. De aceea alegerea este
          explicită, cu explicație, nu ascunsă într-un meniu.
        */}
        <Field
          label="Ce afișează acest ecran"
          hint={
            contentMode === 'kitchen'
              ? 'Board-ul bucătăriei: comenzi în timp real, fără date personale, cu cronometre.'
              : 'Meniul public al restaurantului, ca în sală.'
          }
        >
          {(props) => (
            <Select {...props} value={contentMode} onChange={(event) => setContentMode(event.target.value as 'menu' | 'kitchen')}>
              <option value="menu">Meniul restaurantului</option>
              <option value="kitchen">Comenzi — ecran de bucătărie</option>
            </Select>
          )}
        </Field>

        {contentMode === 'kitchen' ? (
          <div className="flex flex-col gap-4 rounded-[var(--radius-md)] border border-ink-100 bg-surface-3/50 p-4">
            <span className="text-caption font-medium text-ink-600">Setările ecranului de bucătărie</span>

            <Field
              label="Evidențiază comanda după"
              hint="Momentul de la care o comandă apare ca întârziată (bordură roșie și cronometru roșu)"
            >
              {(props) => (
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={1}
                    max={240}
                    value={warnAfter}
                    onChange={(event) => setWarnAfter(Math.max(1, Math.min(240, Number(event.target.value) || 1)))}
                    className="w-24 rounded-[var(--radius-sm)] border border-ink-200 bg-surface px-3 py-2 text-body-s"
                  />
                  <span className="text-body-s text-ink-600">minute</span>
                </div>
              )}
            </Field>

            <label className="flex items-start gap-3 text-body-s text-ink-700">
              <input
                type="checkbox"
                checked={kdsSound}
                onChange={(event) => setKdsSound(event.target.checked)}
                className="mt-1 h-4 w-4 accent-[var(--color-brand-600)]"
              />
              <span>
                <strong className="font-medium">Sunet la comandă nouă</strong>
                <span className="block text-caption text-ink-400">
                  Ecranul sună când apare o comandă. Dacă browserul blochează sunetul, apare pe ecran un buton de
                  activare (o singură atingere).
                </span>
              </span>
            </label>

            <label className="flex items-start gap-3 text-body-s text-ink-700">
              <input
                type="checkbox"
                checked={kdsActions}
                onChange={(event) => setKdsActions(event.target.checked)}
                className="mt-4 h-4 w-4 accent-[var(--color-brand-600)]"
              />
              <span>
                <strong className="font-medium">Bucătăria poate avansa comenzi de pe ecran</strong>
                <span className="block text-caption text-ink-400">
                  Pentru ecrane tactile: acceptă, începe, gata, finalizează. Refuzul unei comenzi rămâne în dashboard
                  (are nevoie de un motiv care ajunge la client).
                </span>
              </span>
            </label>
          </div>
        ) : null}

        <Field label="Luminozitatea ecranului" hint="Se aplică imediat pe televizor; 100% este valoarea normală">
          {() => (
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={20}
                max={100}
                value={brightness}
                onChange={(event) => setBrightness(Number(event.target.value))}
                className="h-1.5 w-full accent-[var(--color-brand-600)]"
              />
              <span className="nums w-12 text-right text-body-s text-ink-600">{brightness}%</span>
              <Button size="sm" variant="ghost" onClick={applyBrightness}>
                Aplică
              </Button>
            </div>
          )}
        </Field>
      </div>
    </Modal>
  );
}

/* ────────────────────────── fișa ecranului: istoric ────────────────────────── */

function DeviceDetailModal({
  device,
  onClose,
  onCommand,
  canCommand,
}: {
  device: Device | null;
  onClose: () => void;
  onCommand: (device: Device, command: string, payload?: Record<string, unknown>) => void;
  canCommand: boolean;
}) {
  const { data, loading } = useResource<{ commands: CommandRow[]; events: StatusEvent[] }>(
    () =>
      device
        ? api.get<{ commands: CommandRow[]; events: StatusEvent[] }>(`/displays/${device.id}/commands`)
        : Promise.resolve({ commands: [], events: [] }),
    [device?.id],
  );

  if (!device) return null;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={device.name}
      description="Ce s-a întâmplat pe acest ecran: starea conexiunii și comenzile trimise."
      footer={
        canCommand ? (
          <>
            <Button variant="ghost" onClick={() => onCommand(device, 'reload_cache')}>
              Reîncarcă memoria
            </Button>
            <Button variant="secondary" onClick={() => onCommand(device, 'refresh')}>
              Reîmprospătează
            </Button>
          </>
        ) : null
      }
    >
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-2">
          <h3 className="font-display text-title text-ink-950">Starea conexiunii</h3>
          {loading ? (
            <Skeleton className="h-24" />
          ) : (data?.events?.length ?? 0) === 0 ? (
            <p className="text-body-s text-ink-600">Nu există încă evenimente înregistrate.</p>
          ) : (
            <ol className="flex flex-col gap-2">
              {data!.events.map((event, index) => (
                <li key={index} className="flex flex-wrap items-center gap-3 text-body-s">
                  <StatusDot tone={event.to_status === 'online' ? 'success' : event.to_status === 'offline' ? 'danger' : 'neutral'} />
                  <span className="text-ink-900">
                    {event.from_status ? `${STATUS_LABEL[event.from_status] ?? event.from_status} → ` : ''}
                    <strong>{STATUS_LABEL[event.to_status] ?? event.to_status}</strong>
                  </span>
                  <span className="text-ink-400">{event.reason ? (REASON_LABEL[event.reason] ?? event.reason) : ''}</span>
                  <span className="ml-auto text-ink-400">{relativeTime(event.occurred_at)}</span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="font-display text-title text-ink-950">Comenzi trimise</h3>
          {loading ? (
            <Skeleton className="h-24" />
          ) : (data?.commands?.length ?? 0) === 0 ? (
            <p className="text-body-s text-ink-600">Nu ai trimis încă nicio comandă către acest ecran.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[var(--color-line)]">
              {data!.commands.map((command) => (
                <li key={command.id} className="flex flex-wrap items-center gap-3 py-2 text-body-s">
                  <span className="text-ink-900">{COMMAND_LABELS[command.command] ?? command.command}</span>
                  <Badge
                    tone={
                      command.status === 'done'
                        ? 'success'
                        : command.status === 'failed'
                          ? 'danger'
                          : command.status === 'sent'
                            ? 'info'
                            : 'neutral'
                    }
                  >
                    {COMMAND_STATUS_LABEL[command.status] ?? command.status}
                  </Badge>
                  <span className="ml-auto text-ink-400">{relativeTime(command.issued_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Modal>
  );
}
